/**
 * CORE-1421 — @-упоминания в чате Live-сессии.
 *
 * Упоминание едет внутри текста сообщения меткой `@[Имя](user:<id>)`. Сервер
 * хранит текст как есть (WatchPartyHub.SendMessage: те же 500 символов,
 * модерация, заморозка), поэтому ни поля, ни миграции под упоминание не
 * понадобилось, а история после перезагрузки приносит метки сама. Связь — по
 * id: две «Анны» в одной сессии остаются двумя разными адресатами.
 *
 * В поле ввода человек видит не метку, а `@Имя`. Какие `@Имя` в черновике —
 * выбранные упоминания, помнит список `MentionPick` рядом с текстом; метки
 * собираются только в момент отправки (`serializeMentions`).
 *
 * Модуль без React и без платформы: общий у веба и натива.
 */

export interface MentionPick {
  /** Позиция `@` в видимом тексте черновика. */
  start: number;
  name: string;
  userId: string;
}

export type MentionSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; name: string; userId: string };

/** Кого можно упомянуть: участник текущей сессии с аккаунтом. */
export interface MentionCandidate {
  userId: string;
  name: string;
  /** CORE-1684 — ник без «@»; подсказка ищется и по нему. */
  nickname?: string;
  avatar?: string;
  isHost: boolean;
  /**
   * Хвост id — только у тех, чьё имя в списке не единственное: одинаковые
   * «Анна» должны различаться хоть чем-то, кроме аватара, которого может не быть.
   */
  tag?: string;
}

/** Строка ростера в том виде, в каком её знают и веб, и натив. */
export interface MentionRosterEntry {
  userId: string;
  name?: string | null;
  nickname?: string | null;
  avatar?: string;
  isHost: boolean;
  isGuest?: boolean;
}

const MAX_NAME_LENGTH = 64;
/** Длиннее запроса подсказки не ищут: человек давно пишет обычный текст. */
const MAX_QUERY_LENGTH = 40;
/** Сколько подсказок показать разом. */
export const MAX_MENTION_SUGGESTIONS = 6;

const MENTION_RE = /@\[([^[\]\n]{1,64})\]\(user:([A-Za-z0-9_-]{1,64})\)/g;
const WORD_CHAR_RE = /[\p{L}\p{N}_]/u;

/** Имя в метке: без скобок и переводов строк, иначе метку не разобрать. */
function cleanName(name: string): string {
  return name.replace(/[[\]\n\r]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_NAME_LENGTH);
}

export function mentionToken(name: string, userId: string): string {
  return `@[${cleanName(name)}](user:${userId})`;
}

export function hasMentionTokens(text: string): boolean {
  MENTION_RE.lastIndex = 0;
  return MENTION_RE.test(text);
}

/** Текст сообщения → куски для отрисовки. Без меток — один текстовый кусок. */
export function parseMentions(text: string): MentionSegment[] {
  const out: MentionSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(MENTION_RE)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ type: 'text', value: text.slice(last, at) });
    out.push({ type: 'mention', name: m[1], userId: m[2] });
    last = at + m[0].length;
  }
  if (last < text.length || out.length === 0) out.push({ type: 'text', value: text.slice(last) });
  return out;
}

/** Метки → `@Имя`: для мест, где текст показывается без разметки. */
export function mentionsToPlainText(text: string): string {
  return text.replace(MENTION_RE, (_m, name: string) => `@${name}`);
}

/** Черновик + выбранные упоминания → текст для отправки. */
export function serializeMentions(text: string, picks: readonly MentionPick[]): string {
  let out = text;
  [...picks].sort((a, b) => b.start - a.start).forEach((p) => {
    const end = p.start + 1 + p.name.length;
    out = out.slice(0, p.start) + mentionToken(p.name, p.userId) + out.slice(end);
  });
  return out;
}

/**
 * Текст с метками → черновик. Нужен, когда отправленное возвращается в поле:
 * модерация отклонила сообщение, и CORE-196 кладёт его обратно.
 */
export function deserializeMentions(serialized: string): { text: string; picks: MentionPick[] } {
  let text = '';
  const picks: MentionPick[] = [];
  parseMentions(serialized).forEach((seg) => {
    if (seg.type === 'text') {
      text += seg.value;
    } else {
      picks.push({ start: text.length, name: seg.name, userId: seg.userId });
      text += `@${seg.name}`;
    }
  });
  return { text, picks };
}

function pickIsIntact(text: string, p: MentionPick): boolean {
  const end = p.start + 1 + p.name.length;
  if (text.slice(p.start, end) !== `@${p.name}`) return false;
  // «@Анна» + дописанная буква — уже другое слово, не упоминание Анны.
  const next = text.charAt(end);
  return next === '' || !WORD_CHAR_RE.test(next);
}

/**
 * Черновик изменился — сдвинуть упоминания правее правки и снять те, которых
 * правка коснулась. Так удаляется выбранное упоминание: стёр хоть букву —
 * упоминания нет, осталось то, что видно в поле.
 */
export function reconcilePicks(prev: string, next: string, picks: readonly MentionPick[]): MentionPick[] {
  if (prev === next || picks.length === 0) return picks.filter((p) => pickIsIntact(next, p));
  let head = 0;
  while (head < prev.length && head < next.length && prev[head] === next[head]) head++;
  let tail = 0;
  while (
    tail < prev.length - head &&
    tail < next.length - head &&
    prev[prev.length - 1 - tail] === next[next.length - 1 - tail]
  ) tail++;
  const editedEnd = prev.length - tail;
  const delta = next.length - prev.length;

  const out: MentionPick[] = [];
  picks.forEach((p) => {
    const end = p.start + 1 + p.name.length;
    let moved: MentionPick | null = null;
    if (end <= head) moved = p;
    else if (p.start >= editedEnd) moved = { ...p, start: p.start + delta };
    if (moved && pickIsIntact(next, moved)) out.push(moved);
  });
  return out;
}

/**
 * Набирается ли сейчас упоминание: `@` в начале слова и после него — начало
 * имени до конца черновика. Ищем только в конце: так пишут почти всегда, а
 * положение курсора в RN на вебе и нативе приходит по-разному.
 */
export function activeMentionQuery(
  text: string,
  picks: readonly MentionPick[],
): { start: number; query: string } | null {
  const start = text.lastIndexOf('@');
  if (start < 0) return null;
  if (start > 0 && !/\s/.test(text[start - 1])) return null;
  if (picks.some((p) => p.start === start)) return null;
  const query = text.slice(start + 1);
  if (query.includes('\n') || query.length > MAX_QUERY_LENGTH) return null;
  return { start, query };
}

function norm(s: string): string {
  return s.toLocaleLowerCase('ru').replace(/ё/g, 'е');
}

/** Совпадение с началом имени или с началом любого слова в нём. */
export function matchMentionCandidates(
  candidates: readonly MentionCandidate[],
  query: string,
): MentionCandidate[] {
  const q = norm(query.trim());
  const hits = q === ''
    ? candidates
    : candidates.filter((c) => {
      const name = norm(c.name);
      // CORE-1684 — и по нику: «@an» находит и Анну, и anna_light.
      const nick = c.nickname ? norm(c.nickname) : '';
      return name.startsWith(q) || name.split(/\s+/).some((w) => w.startsWith(q)) || (!!nick && nick.startsWith(q));
    });
  return hits.slice(0, MAX_MENTION_SUGGESTIONS);
}

/**
 * CORE-1684 — подсказки из двух источников: участники этого чата (уже на
 * руках, отвечают мгновенно) и поиск по всей платформе (приходит с задержкой).
 * Местные первыми, найденные — следом, без повторов по id; account ID и есть
 * адресат, совпадение имён его не подменяет.
 */
export function mergeMentionResults(
  local: readonly MentionCandidate[],
  remote: readonly MentionCandidate[],
  max = MAX_MENTION_SUGGESTIONS,
): MentionCandidate[] {
  const seen = new Set(local.map((c) => c.userId));
  const out = [...local];
  remote.forEach((c) => {
    if (!c.userId || seen.has(c.userId)) return;
    seen.add(c.userId);
    out.push(c);
  });
  return out.slice(0, max);
}

/**
 * Пустое состояние «никого не нашлось» — только пока человек, похоже, ещё
 * набирает имя. Пробел после неизвестного имени значит, что он пишет дальше
 * обычный текст, и висящая плашка ему только мешает.
 */
export function shouldShowMentionEmpty(query: string, matches: readonly MentionCandidate[]): boolean {
  return matches.length === 0 && query.trim() !== '' && !/\s/.test(query);
}

/** Выбор подсказки: `@запрос` в конце черновика заменяется на `@Имя `. */
export function applyMentionPick(
  text: string,
  picks: readonly MentionPick[],
  start: number,
  candidate: Pick<MentionCandidate, 'userId' | 'name'>,
): { text: string; picks: MentionPick[] } {
  const name = cleanName(candidate.name);
  return {
    text: `${text.slice(0, start)}@${name} `,
    picks: [...picks.filter((p) => p.start < start), { start, name, userId: candidate.userId }],
  };
}

/**
 * Ростер сессии → подсказки. Без гостей (аккаунта нет — привязывать не к чему,
 * решение владельца 21.09), без себя и без безымянных строк. Ведущий первым.
 */
export function buildMentionCandidates(
  roster: readonly MentionRosterEntry[],
  selfId: string | null | undefined,
): MentionCandidate[] {
  const seen = new Set<string>();
  const list: MentionCandidate[] = [];
  roster.forEach((r) => {
    const name = cleanName(r.name ?? '');
    if (!r.userId || r.isGuest || r.userId === selfId || !name || seen.has(r.userId)) return;
    seen.add(r.userId);
    const nickname = r.nickname?.trim().replace(/^@/, '') || undefined;
    list.push({ userId: r.userId, name, nickname, avatar: r.avatar, isHost: r.isHost });
  });

  const byName = new Map<string, number>();
  list.forEach((c) => byName.set(norm(c.name), (byName.get(norm(c.name)) ?? 0) + 1));
  list.forEach((c) => {
    // CORE-1684 — ник различает тёзок лучше хвоста id; хвост остаётся тем,
    // у кого ника нет.
    if (!c.nickname && (byName.get(norm(c.name)) ?? 0) > 1) c.tag = `#${c.userId.replace(/-/g, '').slice(-4).toUpperCase()}`;
  });

  return list.sort((a, b) => Number(b.isHost) - Number(a.isHost) || a.name.localeCompare(b.name, 'ru'));
}
